"use client";

import ZoomImage from "@/components/atoms/ZoomImage/ZoomImage";
import sizes from "@/lib/utils/sizes";
import { FormattedMedia } from "@frend-digital/centra/formatting";
import clsx from "clsx";
import Image from "next/image";
import { forwardRef } from "react";
import styles from "./gallery.module.css";

export type Media = {
  uri: string;
  alt: string;
};

export const GalleryRoot = ({
  className,
  children,
  ...rest
}: React.ComponentProps<"div">) => {
  return (
    <div className={clsx(className, styles.gallery)} {...rest}>
      {children}
    </div>
  );
};

export const GalleryViewport = forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => (
  <div className={clsx(className, styles.viewport)} {...props} ref={ref} />
));

GalleryViewport.displayName = "GalleryViewport";

export const GallerySlide = ({
  media,
  priority,
}: {
  media: Media | FormattedMedia;
  priority: boolean;
}) => {
  return (
    <div className={styles.gallerySlide}>
      <ZoomImage
        sizes={sizes(100, 50)}
        className={styles.img}
        src={media ? ("uri" in media ? (media.uri as string) : media.href) : ""}
        alt={media.alt}
        fill
        priority={priority}
      />
    </div>
  );
};

export const GalleryButton = ({
  children,
  className,
  onClick,
  ...rest
}: React.ComponentProps<"button">) => {
  return (
    <button
      className={clsx(styles.galleryButton, className)}
      {...rest}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

export const GalleryThumbsButton = ({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) => {
  return (
    <button
      disabled={disabled}
      className={clsx(styles.thumbsButton, disabled && styles.disabledButton)}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

export type GalleryThumbProps = {
  selected: boolean;
  imgSrc: string;
  alt: string;
  index: number;
  onClick: () => void;
};

export const GalleryThumb: React.FC<GalleryThumbProps> = (props) => {
  const { selected, imgSrc, onClick, alt } = props;

  return (
    <div className={clsx(styles.thumbSlide, selected && styles.selected)}>
      <button
        onClick={onClick}
        className={clsx(styles.thumbSlideButton)}
        type="button"
      >
        <Image
          className={clsx(styles.img)}
          src={imgSrc}
          width={105}
          height={132}
          alt={alt}
        />
      </button>
    </div>
  );
};

export const GalleryDots = ({
  slides,
  scrollTo,
  selectedIndex,
}: {
  slides: unknown[];
  scrollTo?: (index: number) => void;
  selectedIndex: number;
}) => {
  if (slides.length < 2) {
    return null;
  }
  return (
    <nav className={styles.indicators}>
      <div className={styles.inner}>
        {slides.map((_, index) => (
          <button
            onClick={() => scrollTo?.(index)}
            className={clsx(
              styles.indicator,
              selectedIndex === index && styles.selected
            )}
            key={index}
            title={`Slide number ${index}`}
          />
        ))}
      </div>
    </nav>
  );
};
